import * as Sentry from '@sentry/vue'
import RightNavTabs from '@/constant/RightNavTabs'
import EventBus from '@/eventBus'
import config from '@/config'

const SET_RIGHT_NAV_ACTIVE_TAB = 'SET_RIGHT_NAV_ACTIVE_TAB'
const UPDATE_SLIDE_ACTIVE_MUTATION = 'updateSlideActive' // mutations/slideActive.js
const SET_AUDIO_TAB_DISABLED = 'SET_AUDIO_TAB_DISABLED'
const SET_RIGHT_PANEL_CONTAINER_ACTIVE_TAB_ACTION = 'rightPanelContainer/setRightPanelContainerActiveTabAction' // rightPanelContainer.module.js

// state
const _state = {
  rightNavTabNames: RightNavTabs.TAB_NAMES,
  rightNavAllTabs: RightNavTabs.ALL_TABS,
  rightNavActiveTab: config.isEnableAddinPowerpoint ? null : RightNavTabs.TAB_NAMES.CONTENT,
  activeTabHeader: null,
}

// getters
const _getters = {
  rightNavTabNames(state) {
    return state.rightNavTabNames
  },
  rightNavActiveTab(state) {
    return state.rightNavActiveTab
  },
  rightNavAllTabs(state) {
    return state.rightNavAllTabs
  },
  isSingleHeader(state) {
    const activeTab = state.rightNavAllTabs?.[state.rightNavActiveTab?.toUpperCase()]
    return typeof activeTab?.header === 'string'
  },
  isDesignTabActive(state) {
    return state.rightNavActiveTab === state.rightNavTabNames.DESIGN
  },
  isAudioTabActive(state) {
    return state.rightNavActiveTab === state.rightNavTabNames.AUDIO
  },
  isContentTabActive(state, getters, rootState, rootGetters) {
    const isActiveTab = state.rightNavActiveTab === state.rightNavTabNames.CONTENT

    if (!isActiveTab) {
      return false
    }

    // Content tab is disabled when:
    // - Multiple slides are selected, OR
    // - User is in a new blank presentation
    // See RightNav.vue isTabDisabled() for the UI-side logic
    const isMultipleSlidesSelected = rootState.selections?.selectedSlideIds?.length > 1
    const isNewBlankPresentation = rootGetters['blankPresentation/isNewBlankPresentation']
    const isContentTabDisabled = isMultipleSlidesSelected || isNewBlankPresentation

    return !isContentTabDisabled
  },
  isAITabActive(state) {
    return state.rightNavActiveTab === state.rightNavTabNames.AI
  },
  isSettingTabActive(state) {
    return state.rightNavActiveTab === state.rightNavTabNames.SETTINGS
  },
  isAudioTabDisabled(state) {
    return state.isAudioTabDisabled
  },
  activeTabHeader(state) {
    const activeTabData = state.rightNavAllTabs[state.rightNavActiveTab.toUpperCase()]
    return activeTabData ? activeTabData.header : ''
  },
  isPanelActive(state) {
    return (tab) => {
      return state.rightNavActiveTab === tab
    }
  },
  isRightPanelContainerVisible(state, getters, rootState, rootGetters) {
    const { isPresenting, isBackstage } = rootGetters
    const { currentUser } = rootState

    // Hide panel when presenting or in backstage
    if (isPresenting || isBackstage) return false

    // Check basic requirements
    const currentUserId = currentUser?.id
    if (!currentUserId || !state.rightNavActiveTab) return false

    // Don't show panel if the active tab is actually disabled
    // This handles any tab-specific disabled states (e.g., content tab disabled for multiple slides)
    return getters.isContentTabActive ||
      state.rightNavActiveTab !== state.rightNavTabNames.CONTENT
  },
}

// mutations
const mutations = {
  [SET_RIGHT_NAV_ACTIVE_TAB](state, tab) {
    state.rightNavActiveTab = tab
  },
  [SET_AUDIO_TAB_DISABLED](state, value) {
    state.isAudioTabDisabled = value
  },

}

// actions
const actions = {
  setRightNavActiveTabAction({ commit, dispatch, rootState }, tabName) {
    commit(SET_RIGHT_NAV_ACTIVE_TAB, tabName)
    EventBus.$emit('detectScreen')
    if (!tabName) {
      commit(UPDATE_SLIDE_ACTIVE_MUTATION, {
        activeTab: null,
      }, { root: true })
      return
    }
    if (tabName === RightNavTabs.TAB_NAMES.CONTENT) {
      if (!rootState.slideActive.type) {
        commit(UPDATE_SLIDE_ACTIVE_MUTATION, {
          activeTab: 1, // tab Type
        }, { root: true })
      } else {
        commit(UPDATE_SLIDE_ACTIVE_MUTATION, {
          activeTab: 2, // tab Content / Templates
        }, { root: true })
      }
      return
    }
    if (tabName === RightNavTabs.TAB_NAMES.DESIGN) {
      commit(UPDATE_SLIDE_ACTIVE_MUTATION, {
        activeTab: 3, // tab Design
      }, { root: true })
      return
    }
    if (tabName === RightNavTabs.TAB_NAMES.AUDIO) {
      dispatch(SET_RIGHT_PANEL_CONTAINER_ACTIVE_TAB_ACTION,
        rootState.rightPanelContainer.activeTabs.TAB_AUDIO_SLIDE,
        { root: true })
      return
    }
    if (tabName === RightNavTabs.TAB_NAMES.AI) {
      dispatch('aiChatbot/markAIChatbotIndicatorSeenAction', null, { root: true })
    }
  },

  showSlideTypeSelectionAction({ commit, state }) {
    commit(SET_RIGHT_NAV_ACTIVE_TAB, state.rightNavTabNames.CONTENT)
    commit(UPDATE_SLIDE_ACTIVE_MUTATION, {
      activeTab: 1, // tab Type
    }, { root: true })
  },

  setAudioTabDisabled({ commit }, value) {
    commit(SET_AUDIO_TAB_DISABLED, value)
  },

  async openAIPanelAction({ dispatch, rootGetters }) {
    try {
      const isAITabActive = rootGetters && rootGetters['rightNav/isAITabActive']

      if (!isAITabActive) { // Check if AITab is not active to avoid resetting the conversation when reopening.
        await dispatch('setRightNavActiveTabAction', RightNavTabs.TAB_NAMES.AI)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
